import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@core/utils/domUtil";

const buttonVariants = cva("", {
  variants: {
    variant: {
      // shadcn - shadcn 다른 ui에서 참조하는 케이스들이 있어 기존 코드 유지
      default: "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
      destructive: "bg-error text-white shadow-xs hover:bg-error/90 focus-visible:ring-error/20 dark:focus-visible:ring-error/40 dark:bg-error/60",
      outline: "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
      outlineWitoutHover: "border bg-background shadow-xs  dark:bg-input/30 dark:border-input",
      secondary: "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
      ghost: "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
      link: "text-primary underline-offset-4 hover:underline",

      // custom
      bgPoint600: "bg-point-600 text-white",
      bgGray700: "bg-gray-700 text-white",
      bgPurple: "bg-purple-50 text-purple hover:bg-purple/5 active:bg-purple/15",
      borderPoint200: "border border-point-200 bg-white text-point-600 hover:bg-gray-50 active:bg-gray-100",
      borderBgPoint200: "bg-point-100 border border-point-200 text-point-600 hover:bg-point-200",
      borderPoint600: "bg-point-50 border border-point-600 text-point-600 hover:bg-point-100 active:bg-point-200",
      borderBgWhite: "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 active:bg-gray-200",
      borderBgRed: "border border-red bg-red-100 text-red",
      borderBgGray50: "border border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-200 active:bg-gray-300 ",
      borderTextPoint600: "border border-gray-300 bg-white text-point-600 active:bg-gray-100 hover:bg-gray-50",
      text: "text-gray-700 px-0! py-0! h-[auto]! hover:text-gray-800 active:text-gray-800",
      textGray500: "px-0! py-0! h-[auto]! text-gray-500 hover:text-gray-700 active:text-gray-700",
      textUnderLine: "px-0! py-0! h-[auto]! text-gray-700 underline hover:text-gray-800 active:text-gray-800",
      icon: "p-0!",
    },
    size: {
      // shadcn - shadcn 다른 ui에서 참조하는 케이스들이 있어 기존 코드 유지
      default: "h-9 px-4 py-2 has-[>svg]:px-3",
      sm: "h-8 rounded-sm gap-1.5 px-3 has-[>svg]:px-2.5",
      lg: "h-10 rounded-sm px-6 has-[>svg]:px-4",
      icon: "size-9",

      // custom
      small: "h-[32px] px-[12px] py-[8px] rounded-md text-md [&_svg]:max-h-[16px]",
      medium: "h-[38px] lg:h-[40px] px-[10px] lg:px-[16px] py-[8px] rounded-lg lg:text-[15px] text-sm [&_svg]:max-h-[18px]",
      large: "h-[44px] lg:h-[48px] px-[20px] lg:px-[24px] py-[10px] lg:py-[8px] rounded-lg text-md lg:text-lg [&_svg]:max-h-[24px]",
      largeMobileSubmit: cn("h-[44px] lg:h-[48px] lg:px-[24px] py-[10px] lg:py-[8px] rounded-lg text-md lg:text-lg [&_svg]:max-h-[24px]", "max-lg:px-[40px]"),
      largeMobileFooter: cn("h-[44px] lg:h-[48px] lg:px-[24px] py-[10px] lg:py-[8px] rounded-lg text-md lg:text-lg [&_svg]:max-h-[24px]", "max-lg:w-[44%] max-lg:max-w-[160px]"),
      inputLarge: "h-[38px] lg:h-[48px] px-[20px] lg:px-[24px] py-[10px] lg:py-[8px] rounded-lg text-md lg:text-lg [&_svg]:max-h-[24px]",
      xlarge: "h-[58px] px-[50px] py-[12px] rounded-md text-[18px]",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
});

export type ButtonPropsType = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  };

function Button({ className, variant, size, asChild = false, ...props }: ButtonPropsType) {
  const Comp = asChild ? Slot : "button";

  return <Comp data-slot="button" className={cn("focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-error/20 aria-invalid:border-error box-border inline-flex shrink-0 cursor-pointer items-center justify-center gap-[1px] whitespace-nowrap transition-all duration-300 ease-out outline-none focus-visible:ring-[2px] disabled:pointer-events-none disabled:opacity-50", !variant && !size ? className : buttonVariants({ size, variant, className }))} {...props} />;
}

export { Button, buttonVariants };
